import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import styles from './BottomDrawer.module.scss';

export interface BottomDrawerProps {
    open: boolean;
    onClose: () => void;
    /** Title shown in the drawer header. */
    title?: React.ReactNode;
    /** Right-aligned slot in the header (actions, save button, etc). */
    headerRight?: React.ReactNode;
    /**
     * Drawer height as a CSS value. Defaults to 90vh so it leaves a slim peek
     * of the underlying page (Notion / Linear style).
     */
    height?: string;
    children: React.ReactNode;
    /** Optional class for the drawer surface. */
    className?: string;
    /** Optional class to override / extend the body area (e.g. remove default
     *  padding so the contents can own their own scroll). */
    bodyClassName?: string;
    /** Optional id for aria-labelledby etc. */
    id?: string;
    /** Optional slot rendered as a sibling to the right of (header + body),
     *  spanning the full drawer height. Pushes the main column leftward when
     *  present. Used for side-drawers like version history. */
    rightSlot?: React.ReactNode;
    /** Swap the × for a spinner and gate ESC/backdrop dismissal. */
    closingBusy?: boolean;
    /** Hide the header close button (ESC/backdrop still dismiss). Used while a
     *  side pane owns the right edge — e.g. the one-pager comments / history
     *  panes, whose own close affordance would otherwise sit beside the ×. */
    hideCloseBtn?: boolean;
}

export function BottomDrawer({
    open,
    onClose,
    title,
    headerRight,
    height = '90vh',
    children,
    className,
    bodyClassName,
    id,
    rightSlot,
    closingBusy,
    hideCloseBtn,
}: BottomDrawerProps) {
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const closingBusyRef = useRef(closingBusy);
    closingBusyRef.current = closingBusy;
    const guardedClose = useCallback(() => {
        if (closingBusyRef.current) return;
        onCloseRef.current?.();
    }, []);
    // Lock body scroll while the drawer is open so the content scroll lives
    // inside the drawer panel.
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    // Esc closes.
    useEffect(() => {
        if (!open) return;
        function handleKey(e: any) {
            if (e.key === 'Escape') guardedClose();
        }
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [open, guardedClose]);

    if (typeof document === 'undefined') return null;

    return createPortal(
        <div className={clsx(styles.root, open && styles.rootOpen)} aria-hidden={!open}>
            <div className={styles.backdrop} onMouseDown={guardedClose} />
            <div
                className={clsx(styles.panel, className)}
                role="dialog"
                aria-modal="true"
                aria-labelledby={id ? `${id}-title` : undefined}
                style={{ height }}
            >
                <div className={styles.mainCol}>
                    <div className={styles.header}>
                        <div className={styles.headerRow}>
                            <div className={styles.title} id={id ? `${id}-title` : undefined}>
                                {title}
                            </div>
                            <div className={styles.headerRight}>
                                {headerRight}
                                {!hideCloseBtn && (
                                    <button
                                        type="button"
                                        className={clsx(styles.closeBtn, closingBusy && styles.closeBtnBusy)}
                                        onClick={guardedClose}
                                        disabled={closingBusy}
                                        aria-label={closingBusy ? 'Saving' : 'Close'}
                                        aria-busy={closingBusy || undefined}
                                    >
                                        {closingBusy ? (
                                            <span className={styles.closeSpinner} aria-hidden="true" />
                                        ) : (
                                            <CloseRoundedIcon className={styles.closeIcon} aria-hidden="true" />
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={clsx(styles.body, bodyClassName)}>{children}</div>
                </div>
                {rightSlot}
            </div>
        </div>,
        document.body,
    );
}
